import moment from 'moment'
import { IFileProps } from './File'
import { ICalendarEventProps } from './CalendarEvent'
import {
  AppointmentUser,
  AppointmentDoctor,
  AppointmentDatetime,
  AppointmentDatetimes,
  AppointmentProperties,
  AppointmentMessages,
  RelationHospital
} from '@/infrastructure/network/Monitaro/schema/models'

export interface IAppointmentProps {
  id?: string
  hospitalId?: string | null
  userId?: string | null
  doctorId?: string | null
  user?: AppointmentUser | null
  hospital?: RelationHospital | null
  doctor?: AppointmentDoctor | null
  readsCount: number
  datetimes: AppointmentDatetimes
  properties: AppointmentProperties | null
  calendar: any
  department?: string | null
  files?: IFileProps[]
  messages?: AppointmentMessages[] | null
  message?: string | null
  status: string | null
  isHospital: boolean | null
  isAppointment: boolean | null
  isSchedule: boolean | null
  isResult: boolean | null
  isCalendar: boolean | null
  isFormatChanged: boolean | null
  isPlaceChanged: boolean | null
  startedAt: string | null
  endedAt: string | null
  createdAt: string | null
  updatedAt: string | null
  data?: File[] | null
  keeps?: IFileProps[] | null
  input: number
  companyId?: string | null
}

export interface IAppointmentQueryProps {
  status: string[] | null
  filters: string[] | null
  started: string | null
  ended: string | null
  unread: boolean | null
}

export interface IScheduleQueryProps {
  status: string | null
  term: {
    started: string | null
    ended: string | null
  }
  types: string[] | null
  display: 'list' | 'calendar'
  word?: string | null
}

export interface IAppointmentEventProps {
  name: string
  start: string | null
  end: string | null
  color: string
}
export default class AppointmentEntity {
  private _props: IAppointmentProps

  constructor(props?: IAppointmentProps | null) {
    this._props = EmptyAppointmentPropsFactory(props)
  }

  get props(): IAppointmentProps {
    return this._props
  }

  get clone(): IAppointmentProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get hospitalId(): string | null | undefined {
    return this._props.hospitalId
  }

  get userId(): string | null | undefined {
    return this._props.userId
  }

  get doctorId(): string | null | undefined {
    return this._props.doctorId
  }

  get user(): AppointmentUser | null | undefined {
    return this._props.user
  }

  get hospital(): RelationHospital | null | undefined {
    return this._props.hospital
  }

  get doctor(): AppointmentDoctor | null | undefined {
    return this._props.doctor
  }

  get unread(): boolean {
    if (this._props.status === 'proposal' && this._props.isHospital) {
      return false
    }
    if (this._props.status === 'adjustment' && !this._props.isHospital) {
      return false
    }
    return this._props.readsCount === 0
  }

  get datetimes(): AppointmentDatetimes | null | undefined {
    return this._props.datetimes
  }

  get proposed(): string[] | null | undefined {
    moment.locale('ja')
    return this._props.datetimes?.proposed?.flatMap((prop) => {
      if (prop.started && prop.ended) {
        return moment(prop.started).format('YYYY年M月DD日(dd) HH:mm') + ' - ' + moment(prop.ended).format('HH:mm')
      }
      return []
    })
  }

  get properties(): AppointmentProperties | null | undefined {
    return this._props.properties
  }

  get department(): string | null | undefined {
    return this._props.department
  }

  get files(): IFileProps[] | undefined {
    return this._props.files
  }

  get messages(): AppointmentMessages[] | undefined | null {
    return this._props.messages
  }

  get message(): string {
    return this._props.messages?.slice(-1)[0]?.message || ''
  }

  get isHospital(): boolean | null {
    return this._props.isHospital
  }

  get isAppointment(): boolean | null {
    return this._props.isAppointment
  }

  get isSchedule(): boolean | null {
    return this._props.isSchedule
  }

  get isResult(): boolean | null {
    return this._props.isResult
  }

  get isFormatChanged(): boolean | null {
    return this._props.isFormatChanged
  }

  get isPlaceChanged(): boolean | null {
    return this._props.isPlaceChanged
  }

  get datetime(): string | null {
    const started = this._props.startedAt ? moment(this._props.startedAt).format('M/D HH:mm') + '〜' : ''
    const ended = this._props.endedAt ? moment(this._props.endedAt).format('HH:mm') : ''
    return started + ended
  }

  get year(): string | null {
    const year = this._props.startedAt ? moment(this._props.startedAt).format('YYYY') : null
    return moment().year().toString() === year ? null : year
  }

  get date(): string | null {
    return this._props.startedAt ? moment(this._props.startedAt).format('YYYY-MM-DD') : null
  }

  get started(): string | null {
    return this._props.startedAt ? moment(this._props.startedAt).format('H:mm') : null
  }

  get ended(): string | null {
    return this._props.endedAt ? moment(this._props.endedAt).format('H:mm') : null
  }

  get startedAt(): string | null {
    return this._props.startedAt ? moment(this._props.startedAt).format('YYYY年M月DD日 HH:mm') : null
  }

  get endedAt(): string | null {
    return this._props.endedAt
  }

  get createdAt(): string | null {
    return moment(this._props.createdAt).format('YYYY年M月DD日 HH:mm')
  }

  get updatedAt(): string | null {
    return moment(this._props.updatedAt).format('YYYY/MM/DD HH:mm')
  }

  get status(): any | null | undefined {
    // isHospital=0 (MRから病院への申請)の場合
    if (!this._props.isHospital) {
      switch (this._props.status) {
        case 'proposal':
          return { text: '要対応', value: 'proposal', color: 'alert' }
        case 'confirmed':
          return { text: '確定', value: 'confirmed', color: 'secondary' }
        case 'adjustment':
          return { text: '申請中', value: 'adjustment', color: 'primary' }
        case 'refusal':
          return { text: '辞退', value: 'refusal', color: 'm-dark-gray' }
      }
    }

    // isHospital=1 (病院からMRへの面談依頼)の場合
    if (this._props.isHospital) {
      switch (this._props.status) {
        case 'proposal':
          return { text: '申請中', value: 'proposal', color: 'primary' }
        case 'confirmed':
          return { text: '確定', value: 'confirmed', color: 'secondary' }
        case 'adjustment':
          return { text: '要対応', value: 'adjustment', color: 'alert' }
        case 'refusal':
          return { text: '辞退', value: 'refusal', color: 'm-dark-gray' }
      }
    }

    // フォールバック
    return { text: this._props.status, value: this._props.status, color: 'primary' }
  }

  get schedule(): any | null | undefined {
    if (this._props.isResult) {
      return { text: '訪問結果', color: 'primary' }
    }
    if (this._props.isSchedule) {
      return { text: '訪問予定', color: 'secondary' }
    }
  }

  get result(): any | null | undefined {
    if (this._props.isResult) {
      return { text: '訪問結果', color: 'primary' }
    }
  }

  get scheduleStatus(): any | null | undefined {
    // 訪問結果の場合
    if (this._props.isResult) {
      return { text: '訪問結果', value: 'result', color: 'primary', textColor: 'white' }
    }

    // Schedule用の特別なステータス表示
    if (this._props.isSchedule) {
      if (this._props.isAppointment) {
        return { text: '承認済', value: 'approved', color: 'm-blue', textColor: 'white' }
      } else {
        return { text: '申告済', value: 'declared', color: 'm-green', textColor: 'white' }
      }
    }

    // スケジュール以外の場合は通常のステータスを返す
    return this.status
  }

  get proposal(): boolean {
    return this._props.status === 'proposal'
  }

  get adjustment(): boolean {
    return this._props.status === 'adjustment'
  }

  get confirmed(): boolean {
    return this._props.status === 'confirmed'
  }

  get refusal(): boolean {
    return this._props.status === 'refusal'
  }

  get format(): string | null | undefined {
    return this._props.properties?.format
  }

  get type(): string | null | undefined {
    return this._props.properties?.type
  }

  get place(): string | null | undefined {
    // TODO: リンクの処理を外だしできるように修正
    if (this._props.properties?.format === 'オンライン') {
      const place = this._props.properties?.place
      if (place) {
        // プロトコルがない場合は https:// を追加
        const url = place.startsWith('http://') || place.startsWith('https://') ? place : `https://${place}`
        return `<a href="${url}" target="_blank">オンライン会議</a>`
      }
    }
    return this._props.properties?.place
  }

  get remote(): boolean {
    return this._props.properties?.format === 'オンライン'
  }

  get interviewer(): string {
    return this._props.properties?.interviewer || this.doctor?.fullname || ''
  }

  get purpose(): string | null | undefined {
    return this._props.properties?.purpose
  }

  get content(): string | null | undefined {
    return this._props.properties?.content
  }

  get visitor(): number {
    return this._props.properties?.visitor || 0
  }

  get other(): string | null | undefined {
    return this._props.properties?.other
  }

  get event(): ICalendarEventProps | null {
    if (this._props.status === 'confirmed') {
      // 表示名の作成
      const name = []
      if (this._props.properties?.type ?? null) {
        name.push(this._props.properties?.type)
      }
      name.push(this.user?.organization)
      name.push(this.user?.fullname)

      return {
        id: this._props.id,
        google: this.eventId(this._props.calendar?.google),
        microsoft: this.eventId(this._props.calendar?.microsoft),
        name: this._props.isCalendar ? this._props.properties?.purpose : name.join(' '),
        start: this._props.startedAt,
        end: this._props.endedAt,
        color: this._props.isCalendar ? '#FFAB40' : 'm-light-blue',
        appointment: this._props
      }
    }
    return null
  }

  // 訪問予定/結果のカレンダー表示用。アポ管理と異なりstatusを問わず表示する
  get scheduleEvent(): ICalendarEventProps {
    const name = []
    if (this._props.properties?.type) {
      name.push(this._props.properties.type)
    }
    name.push(this.user?.organization)
    name.push(this.user?.fullname)

    return {
      id: this._props.id,
      name: name.filter(Boolean).join(' '),
      start: this._props.startedAt,
      end: this._props.endedAt,
      // 面談形式による色分け。textColor()は実際のHEX値でないとコントラストを正しく計算できないため、テーマトークン名ではなく実際の色コードを使う
      color: this._props.properties?.format === 'オンライン' ? '#E6ECFB' : '#0F3EB6',
      appointment: this._props
    }
  }

  check(type: keyof AppointmentEntity): boolean {
    return !!(this as any)[type]
  }

  eventId(data: any): string[] {
    const events = (data || []).filter((prop: any) => prop.owner_id === this._props.doctorId).map((prop: any) => prop.event_id)
    return events || []
  }
}

const header = {
  date: { text: '日時', value: 'date', sortable: true, class: 'min' },
  user: { text: '訪問者', value: 'user', sortable: true },
  interviewer: { text: '面談相手', value: 'interviewer', sortable: true, class: 'space-normal' },
  purpose: { text: '目的', value: 'purpose', sortable: true },
  visitor: { text: '同行者', value: 'visitor', class: 'space-normal' },
  place: { text: '面談', value: 'place', sortable: true },
  status: { text: '申請', value: 'status' },
  attachment: { text: '', value: 'attachment', class: 'min' }
}

export const headers = [header.date, header.user, header.interviewer, header.purpose, header.visitor, header.place, header.status, header.attachment]

export const results = [header.date, header.user, header.interviewer, header.purpose, header.visitor, header.place, header.attachment]

export const inputs = [
  { text: 'アポイントメント/立ち会い', value: 0 },
  { text: '訪問予定', value: 1 },
  { text: '訪問結果', value: 2 }
]

export const types = ['面談', '説明会', '手術立会い', 'その他']
export const purposes = ['情報提供活動', '説明会の実施', '講演会の案内', '資材提供', '有害事象対応', '流通関係', '挨拶、引き継ぎ']
export const opes = [
  '新製品の試用',
  'バージョンアップ',
  '不具合／故障修理対応',
  '担当者交代',
  '看護師への説明',
  'メンテナンス対応',
  '緊急対応',
  '手術撮影',
  'その他'
]
export const formats = ['訪問', 'オンライン']

// 新規申請時に表示する候補日時の数
export const PROPOSAL_COUNT = 3

// 候補日時の初期値。日付は未入力とし、時刻の初期表示は入力欄側で補う
export const createProposal = (): AppointmentDatetime => ({
  started: null,
  ended: null
})

// 候補日時の入力欄をまとめて作る。要素ごとに別オブジェクトを返す
export const createProposals = (count: number = PROPOSAL_COUNT): AppointmentDatetime[] => Array.from({ length: count }, () => createProposal())

// 送信対象にできるのは開始・終了がそろった候補だけ
export const validProposals = (proposed?: AppointmentDatetime[] | null): AppointmentDatetime[] =>
  (proposed || []).filter((prop) => !!prop?.started && !!prop?.ended)

export const EmptyAppointmentPropsFactory = (props?: Partial<IAppointmentProps> | null): IAppointmentProps => ({
  calendar: null,
  readsCount: 0,
  status: null,
  isHospital: false,
  isAppointment: false,
  isSchedule: false,
  isResult: false,
  isCalendar: false,
  isFormatChanged: false,
  isPlaceChanged: false,
  startedAt: null,
  endedAt: null,
  createdAt: null,
  updatedAt: null,
  input: 0,
  companyId: null,
  message: null,
  ...props,
  datetimes: EmptyAppointmentDatetimesFactory(props?.datetimes),
  properties: EmptyAppointmentPropertiesFactory(props?.properties)
})

export const EmptyAppointmentQueryPropsFactory = (props?: Partial<IAppointmentQueryProps> | null): IAppointmentQueryProps => ({
  status: null,
  filters: null,
  started: null,
  ended: null,
  unread: null,
  ...props
})

export const EmptyScheduleQueryPropsFactory = (props?: Partial<IScheduleQueryProps> | null): IScheduleQueryProps => ({
  status: 'schedule',
  term: {
    started: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    ended: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
  },
  types: null,
  ...props,
  // 保存値がnullで返る場合があるため、'calendar'以外は'list'に正規化する
  display: props?.display === 'calendar' ? 'calendar' : 'list'
})

export const EmptyAppointmentDatetimeFactory = (props?: Partial<AppointmentDatetime> | null): AppointmentDatetime => ({
  started: null,
  ended: null,
  ...props
})

export const EmptyAppointmentDatetimesFactory = (props?: Partial<AppointmentDatetimes> | null): AppointmentDatetimes => ({
  confirmed: null,
  proposed: createProposals(),
  ...props
})

export const EmptyAppointmentPropertiesFactory = (props?: Partial<AppointmentProperties> | null): AppointmentProperties => ({
  type: null,
  interviewer: null,
  department: null,
  purpose: null,
  place: null,
  format: '訪問',
  content: null,
  visitor: null,
  other: null,
  result: null,
  ...props
})
